import { SignalingClient, Role } from 'amazon-kinesis-video-streams-webrtc';
import { eventChannel } from 'redux-saga';
import { getLogger } from '../../logger/index';

const logger = getLogger('communicationVideo');

export function createPeerConnection(settings, RTCPeerConnection) {
  return new RTCPeerConnection({
    iceServers: settings.iceServers.map(iceServer => ({
      urls: iceServer.Uris,
      username: iceServer.Username,
      credential: iceServer.Password,
    })),
    iceTransportPolicy: 'relay',
  });
}

export function createSignalingClient(settings, config = {}) {
  const request = {
    channelARN: settings.channelARN,
    channelEndpoint: settings.channelEndpoints.WSS[0],
    role: config.isMaster ? Role.MASTER : Role.VIEWER,
    region: settings.region,
    credentials: {
      accessKeyId: settings.accessKeyId,
      secretAccessKey: settings.secretAccessKey,
      sessionToken: settings.sessionToken,
    },
  };
  if (!config.isMaster) {
    request.clientId = settings.to;
  }
  logger.debug('createSignalingClient', request);
  return new SignalingClient(request);
}

export const getVideoConstraints = (options = {}, isMobile = false) => {
  const video = { deviceId: options.videoSource ? { exact: options.videoSource } : true };

  if (isMobile) {
    video.facingMode = { exact: options.facingMode ? options.facingMode : 'user' };
    if (video.facingMode === 'user') {
      video.width = { ideal: 720 };
    } else {
      video.width = { ideal: 1280 };
    }

    return video;
  }

  video.width = { ideal: 1280 };
  video.height = { ideal: 720 };

  return video;
};

// available options:
//   - audioSource
//   - videoSource
//   - facingMode
export const getUserMediaConstraints = (type = 'video', options = {}, isMobile) => {
  const audio = { deviceId: options.audioSource ? { exact: options.audioSource } : true };

  if (type === 'audio') {
    return { audio };
  }

  return { audio, video: getVideoConstraints(options, isMobile) };
};

export const getUserMedia = (mediaDevices, type, options = {}, isMobile) => {
  const constraints = getUserMediaConstraints(
    type,
    {
      ...options,
    },
    isMobile
  );
  logger.debug('mediaDevices.getUserMedia - constraints:', constraints);
  return mediaDevices.getUserMedia(constraints);
};

export function createSignalingClientChannel(signalingClient) {
  logger.debug('Setup SignalingClient channel');
  return eventChannel(emit => {
    // Once the signaling channel connection is open, connect to the webcam and create an offer to send to the master
    signalingClient.on('open', args => {
      logger.debug('signalingClient open');
      emit({ eventName: 'open', args });
    });
    // When the SDP answer is received back from the master, add it to the peer connection.
    signalingClient.on('sdpAnswer', args => {
      logger.debug('signalingClient sdpAnswer');
      emit({ eventName: 'sdpAnswer', args });
    });
    // When an ICE candidate is received from the master, add it to the peer connection.
    signalingClient.on('iceCandidate', args => {
      logger.debug('signalingClient iceCandidate');
      emit({ eventName: 'iceCandidate', args });
    });

    signalingClient.on('close', args => {
      logger.debug('signalingClient close');
      emit({ eventName: 'close', args });
    });

    signalingClient.on('error', err => {
      logger.error('signalingClient error', err);
      emit({ eventName: 'error', args: err });
    });

    logger.debug('SignalingClient channel established.');

    return () => {
      // signalingClient.close();
      logger.info('SignalingClient channel closed');
    };
  });
}

export function createPeerConnectionChannel(peerConnection) {
  logger.debug('Setup peerConnection channel');
  return eventChannel(emit => {
    // Send any ICE candidates generated by the peer connection to the other peer
    peerConnection.addEventListener('icecandidate', args => {
      logger.debug('peerConnection icecandidate', args);
      emit({ eventName: 'close', args });
    });

    // As remote tracks are received, add them to the remote view
    peerConnection.addEventListener('track', args => {
      logger.debug('peerConnection track', args);
      emit({ eventName: 'track', args });
    });

    peerConnection.addEventListener('iceconnectionstatechange', args => {
      logger.debug('peerConnection iceconnectionstatechange', args);
      emit({ eventName: 'iceconnectionstatechange', args });
    });

    logger.debug('PeerConnection channel established.');
    return () => {
      // peerConnection.close();
      logger.info('PeerConnection channel closed');
    };
  });
}

export function connectStreamToPeerConnection(stream, peerConnection) {
  if (peerConnection.addTrack) {
    stream.getTracks().forEach(track => {
      logger.debug('Connect stream track to peerConnection', track);
      peerConnection.addTrack(track, stream);
      // senderMapping[track.kind] = sender; // TODO
    });
  } else {
    logger.debug('Connect stream to peerConnection', stream);
    peerConnection.addStream(stream);
  }
}
